<script setup lang="ts">
import { onMounted, ref, onBeforeUnmount, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useColors } from 'vuestic-ui'
import { useAuthStore } from './stores/auth-store'
import { fetchUserProfile } from './services/auth-service'
import { sseService } from './services/sse-service'
import { useEventsStore } from './stores/events-store'

const authStore = useAuthStore()
const router = useRouter()
const eventStore = useEventsStore()
const isLoading = ref(true)
const { applyPreset } = useColors()

const THEME_STORAGE_KEY = 'vuestic-theme-preference'

const logout = async () => {
  import('./services/auth-service').then(({ logout }) => {
    logout(router)
  })
}

defineExpose({ logout })

// Watch for authentication state changes and connect/disconnect SSE accordingly
watch(
  () => authStore.isLoggedIn,
  (isLoggedIn) => {
    if (isLoggedIn && !eventStore.getConnectionStatus) {
      console.log('User authenticated, connecting to SSE...')
      sseService.connect()
    } else {
      console.log('User logged out, disconnecting from SSE...')
      sseService.disconnect()
    }
  },
  { immediate: false }, // Don't run immediately since we handle initial state in onMounted
)

onMounted(async () => {
  // Load saved theme preference before anything else
  const savedTheme = localStorage.getItem(THEME_STORAGE_KEY)
  if (savedTheme && (savedTheme === 'dark' || savedTheme === 'light')) {
    applyPreset(savedTheme)
  }

  // Register the loading state before any async operation
  isLoading.value = true

  if (authStore.accessToken) {
    try {
      await fetchUserProfile()
      if (router.currentRoute.value.name === 'login') {
        router.push({ name: 'dashboard' })
      }
      if (eventStore.getConnectionStatus === false) {
        console.log('Connecting to SSE on app start...')
        sseService.connect()
      }
    } catch (error) {
      console.error('Failed to refresh token on app start:', error)
      router.push({ name: 'login' })
    }
  } else if (
    authStore.isLoggedIn === false &&
    router.currentRoute.value.meta &&
    router.currentRoute.value.meta.isPublic
  ) {
    router.push({ name: 'privacy-policy' })
    // Allow access to public routes
  } else if (
    authStore.isLoggedIn === false &&
    router.currentRoute.value.name !== 'login' &&
    router.currentRoute.value.name !== 'signup' &&
    router.currentRoute.value.name !== 'recover-password' &&
    router.currentRoute.value.name !== 'recover-password-email'
  ) {
    router.push({ name: 'login' })
  }
  // Note: SSE connection is now handled by the reactive watcher
  // Set loading to false when done
  isLoading.value = false
})

// Clean up SSE connection when the app is unmounted
onBeforeUnmount(() => {
  sseService.disconnect()
})
</script>

<template>
  <div>
    <div v-if="isLoading" class="app-loading">
      <div class="loading-spinner"></div>
    </div>
    <RouterView v-else />
  </div>
</template>

<style lang="scss">
#app {
  font-family: 'Inter', Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.app-loading {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  height: calc(100vw - 8px);
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: white;
  z-index: 9999;
  overflow: hidden;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 5px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: #7367f0;
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

body {
  margin: 0;
  min-width: 20rem;
}
</style>
